import React, { useMemo, useState } from 'react'
import { sendPixe, type SendInput } from '../../api/wallet'

function normalizeAmountInput(s: string): string {
  if (!s) return ''
  let t = s.trim()
  t = t.replace(/,/g, '.')
  if (t.startsWith('.')) t = '0' + t
  t = t.replace(/\s+/g, '')
  const parts = t.split('.')
  if (parts.length > 2) t = parts[0] + '.' + parts.slice(1).join('')
  return t
}

export default function SendModal({ open, onClose, onSent }: { open: boolean; onClose: () => void; onSent?: (result?: any) => void }) {
  const [to, setTo] = useState('')
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [step, setStep] = useState<'form' | 'review'>('form')
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const toClean = (to || '').trim()
  const isLikelySS58 = useMemo(() => /^5[1-9A-HJ-NP-Za-km-z]{20,}$/.test(toClean), [toClean])
  const toValid = toClean.length > 0

  const normalized = useMemo(() => normalizeAmountInput(amount), [amount])
  const amtNum = useMemo(() => {
    const n = Number(normalized)
    return Number.isFinite(n) ? n : NaN
  }, [normalized])
  const amountValid = Number.isFinite(amtNum) && amtNum > 0
  const canReview = toValid && amountValid

  function resetLocal() {
    setError(null)
    setStep('form')
    setSending(false)
  }

  async function handleReview() {
    if (!canReview || sending) return
    setError(null)
    setStep('review')
  }

  async function handleConfirmSend() {
    if (sending) return
    setSending(true)
    setError(null)
    const payload: SendInput = {
      to: toClean,
      amount: Number(amtNum.toFixed(12)),
      memo: memo.trim() || undefined,
    }
    try {
      const idemKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
      const requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
      const result = await sendPixe(payload, {
        idempotencyKey: idemKey,
        clientRequestId: requestId,
        clientAction: 'wallet_send_pixe',
      })
      const okStatuses = new Set(['submitted', 'in_block', 'finalized', 'confirmed'])
      if (!result?.tx_hash || !result?.journal_entry_id || !okStatuses.has(String(result?.status || ''))) {
        throw new Error('Transfer was not confirmed by backend. Please retry.')
      }
      onSent?.(result)
      resetLocal()
      onClose()
    } catch (e: any) {
      const code = String(e?.response?.data?.error || '').trim()
      const detail = e?.response?.data?.detail || e?.message || 'Failed to send PIXE'
      if (code === 'PIXECHAIN_UNAVAILABLE' || code === 'PIXECHAIN_BAD_GATEWAY') {
        setError('PixeChain is temporarily unavailable. Please try again in a moment.')
      } else {
        setError(detail)
      }
      setStep('review')
    } finally {
      setSending(false)
    }
  }

  function handleCancel() {
    if (sending) return
    resetLocal()
    onClose()
  }

  if (!open) return null
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={handleCancel} />
      <div className="absolute inset-x-0 bottom-0 md:inset-0 md:flex md:items-center md:justify-center">
        <div className="md:w-[560px] w-full bg-neutral-950 border-t md:border border-white/10 rounded-t-2xl md:rounded-2xl p-4">
          <div className="w-10 h-1 rounded bg-white/20 mx-auto mb-2 md:hidden" />
          <div className="flex items-center justify-between mb-2">
            <div className="text-white font-extrabold text-lg">{step === 'form' ? 'Send PIXE' : 'Review Transfer'}</div>
            <button onClick={handleCancel} className="px-2 py-1 rounded-md text-white/90 hover:bg-white/10">✕</button>
          </div>

          {step === 'form' ? (
            <>
              <div className="text-white/70 text-sm mb-3">On-chain transfer on <span className="text-white font-extrabold">PixeChain</span>. Transactions are irreversible.</div>
              <label className="block mb-3">
                <div className="text-white/70 text-sm mb-1">Recipient (SS58 / @handle / username / id)</div>
                <input
                  value={to}
                  onChange={(e)=>setTo(e.target.value)}
                  placeholder={isLikelySS58 ? '5F… (PixeChain SS58)' : '@alex or 5F…'}
                  className={`w-full rounded-lg bg-white/5 border px-3 py-2 outline-none ${isLikelySS58 ? 'border-emerald-500/50' : 'border-white/10'}`}
                />
                {toClean && (
                  <div className={`mt-1 text-xs ${isLikelySS58 ? 'text-emerald-300' : 'text-white/60'}`}>{isLikelySS58 ? 'Detected SS58 address' : 'Will resolve to a user / address'}</div>
                )}
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="text-white/70 text-sm mb-1">Amount (PIXE)</div>
                  <input
                    value={amount}
                    onChange={(e)=>setAmount(e.target.value)}
                    placeholder="0.00"
                    className={`w-full rounded-lg bg-white/5 border px-3 py-2 outline-none ${amount.length===0 ? 'border-white/10' : amountValid ? 'border-emerald-500/50' : 'border-rose-500/50'}`}
                  />
                  {!amountValid && amount.length>0 && (
                    <div className="text-rose-300 text-xs mt-1">Enter a positive number (e.g., 1.5). If your keyboard shows a comma, use a dot.</div>
                  )}
                </label>
                <label className="block">
                  <div className="text-white/70 text-sm mb-1">Memo (optional)</div>
                  <input value={memo} onChange={(e)=>setMemo(e.target.value)} placeholder="For coffee ☕" className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none" />
                </label>
              </div>

              <div className="mt-4 flex gap-2 justify-end">
                <button onClick={handleCancel} className="px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10">Cancel</button>
                <button onClick={handleReview} disabled={!canReview} className={`px-4 py-2 rounded-lg font-semibold ${canReview ? 'bg-sky-500 hover:bg-sky-600 text-white' : 'bg-white/10 text-white/60 cursor-not-allowed'}`}>Review</button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 mb-3">
                <div className="text-white/70 text-sm mb-1">You are sending</div>
                <div className="text-white text-2xl font-extrabold">{amtNum} PIXE</div>
                <div className="text-white/70 text-sm mt-2">to</div>
                <div className="text-white font-mono break-all">{toClean}</div>
                {memo.trim() && (<div className="text-white/80 text-sm mt-2">Memo: {memo.trim()}</div>)}
              </div>
              {error && <div className="text-rose-300 text-sm mb-2">{error}</div>}
              <div className="flex gap-2 justify-end">
                <button onClick={()=>setStep('form')} disabled={sending} className="px-4 py-2 rounded-lg border border-white/15 text-white/90 hover:bg-white/10 disabled:opacity-60">Back</button>
                <button onClick={handleConfirmSend} disabled={sending} className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold disabled:opacity-60">{sending ? 'Submitting...' : 'Confirm & Send'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}


